import { PASSWORD_HINT } from './passwordPolicy';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * VERAX: Sesión en el portal
 *
 * El token de acceso (5 min) vive solo en memoria: no se escribe en
 * localStorage ni en cookies legibles por JavaScript, así un XSS no puede
 * llevárselo para usarlo después. El token de renovación está en una cookie
 * httpOnly que el navegador envía solo a /api/auth.
 *
 * Inactividad: tras 5 minutos sin usar el teclado, el ratón o la pantalla
 * táctil, la sesión se cierra. Un minuto antes aparece un aviso. El token solo
 * se renueva si hubo actividad desde la última renovación, y el servidor
 * rechaza renovar una sesión inactiva, así que el cierre no depende de que
 * esta pestaña siga abierta.
 */

export const IDLE_MS = 5 * 60 * 1000;
const WARN_MS = 60 * 1000;
const ACT_KEY = 'verax.ultimaActividad';
const CLOSE_KEY = 'verax.cierreSesion';
let lastActivity = Date.now();
let lastRefreshAt = Date.now();
// La marca se comparte entre pestañas para que una pestaña olvidada no cierre
// la sesión de quien trabaja en otra. Si el almacenamiento falla, se usa solo
// la actividad de esta pestaña.
const markActivity = () => {
  const now = Date.now();
  if (now - lastActivity < 1000) return;
  lastActivity = now;
  try { localStorage.setItem(ACT_KEY, String(now)); } catch { /* solo esta pestaña */ }
};
const lastSeen = () => {
  let t = lastActivity;
  try { const v = Number(localStorage.getItem(ACT_KEY)); if (Number.isFinite(v) && v > t) t = v; } catch { /* solo esta pestaña */ }
  return t;
};
const tokenExpiresAt = (t: string | null): number => {
  if (!t) return 0;
  try {
    const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (p.exp ?? 0) * 1000;
  } catch { return 0; }
};

export const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
axios.defaults.withCredentials = true;

export type Role = 'SUPER_ADMIN' | 'ADMIN' | 'ANALYST' | 'OPERATOR' | 'VIEWER';
export interface SessionUser { id: string; email: string; name: string; role: Role; companyId: string | null }

export const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: 'Superadministrador', ADMIN: 'Dueño del proceso', ANALYST: 'Analista',
  OPERATOR: 'Operador', VIEWER: 'Consulta'
};
const RANK: Record<Role, number> = { SUPER_ADMIN: 5, ADMIN: 4, ANALYST: 3, OPERATOR: 2, VIEWER: 1 };
export const atLeast = (u: SessionUser | null, r: Role) => !!u && RANK[u.role] >= RANK[r];

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionLost: () => void = () => {};

const decode = (t: string): SessionUser | null => {
  try {
    const b64 = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const p = JSON.parse(new TextDecoder().decode(bytes));
    return { id: p.sub, email: p.email, name: p.name, role: p.role, companyId: p.companyId ?? null };
  } catch { return null; }
};

const tryRefresh = (): Promise<boolean> => {
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, {}, { headers: { 'x-verax-skip': '1' } })
      .then(r => { accessToken = r.data.accessToken; lastRefreshAt = Date.now(); return true; })
      .catch(() => { accessToken = null; return false; })
      .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
};

axios.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  if (accessToken && !cfg.headers['x-verax-skip']) cfg.headers.Authorization = `Bearer ${accessToken}`;
  return cfg;
});

axios.interceptors.response.use(undefined, async (error: AxiosError) => {
  const cfg = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
  const url = cfg?.url || '';
  if (error.response?.status === 401 && cfg && !cfg._retry && !url.includes('/auth/')) {
    cfg._retry = true;
    if (await tryRefresh()) {
      cfg.headers.Authorization = `Bearer ${accessToken}`;
      return axios(cfg);
    }
    onSessionLost();
  }
  return Promise.reject(error);
});

interface Ctx { user: SessionUser | null; logout: () => Promise<void> }
const AuthContext = createContext<Ctx>({ user: null, logout: async () => {} });
export const useAuth = () => useContext(AuthContext);

const errorText = (e: any) => e?.response?.data?.error || 'No fue posible completar la operación';

const Field: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: string }> = ({ label, ...p }) => (
  <label className="block mb-4">
    <span className="block text-xs uppercase tracking-wider text-gray-400 mb-1">{label}</span>
    <input {...p} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20" />
  </label>
);

type Step =
  | { k: 'password' }
  | { k: 'mfa'; token: string }
  | { k: 'setup'; token: string; qr?: string; manualKey?: string };

const Login: React.FC<{ onDone: (token: string) => void; aviso?: string }> = ({ onDone, aviso }) => {
  const [step, setStep] = useState<Step>({ k: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      setPassword('');
      if (r.data.step === 'MFA') setStep({ k: 'mfa', token: r.data.mfaToken });
      else {
        const s = await axios.post(`${API}/auth/mfa/setup`, { setupToken: r.data.setupToken });
        setStep({ k: 'setup', token: r.data.setupToken, qr: s.data.qr, manualKey: s.data.manualKey });
      }
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = step.k === 'mfa'
        ? await axios.post(`${API}/auth/mfa`, { mfaToken: step.token, code })
        : await axios.post(`${API}/auth/mfa/setup/confirm`, { setupToken: (step as any).token, code });
      onDone(r.data.accessToken);
    } catch (err) {
      setError(errorText(err)); setCode('');
      if ((err as any)?.response?.status === 401 && /expir/i.test(errorText(err))) setStep({ k: 'password' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src="/verax-mark.svg" alt="" width={44} height={44} className="rounded-lg" />
          <div>
            <h1 className="text-2xl font-bold tracking-[0.2em]">VERAX</h1>
            <p className="text-xs text-gray-400">Detección de suplantación de marca y directivos</p>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 shadow-2xl">
          {step.k === 'password' && (
            <form onSubmit={submitPassword}>
              <h2 className="text-lg font-semibold mb-1">Ingreso</h2>
              <p className="text-xs text-gray-500 mb-5">El segundo factor es obligatorio para todos los roles.</p>
              {aviso && <p role="status" className="text-sm text-amber-300 bg-amber-950/40 border border-amber-900/60 rounded px-3 py-2 mb-4">{aviso}</p>}
              <Field label="Correo" type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} />
              <Field label="Contraseña" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />
              <p className="text-[11px] text-gray-500 -mt-2 mb-4">{PASSWORD_HINT}</p>
              {error && <p role="alert" className="text-sm text-red-400 mb-3">{error}</p>}
              <button disabled={busy} className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 py-2.5 rounded font-semibold">
                {busy ? 'Verificando…' : 'Continuar'}
              </button>
            </form>
          )}
          {step.k !== 'password' && (
            <form onSubmit={submitCode}>
              <h2 className="text-lg font-semibold mb-1">
                {step.k === 'mfa' ? 'Código de verificación' : 'Activa tu segundo factor'}
              </h2>
              {step.k === 'setup' ? (
                <div className="mb-4">
                  <p className="text-xs text-gray-400 mb-3">
                    Escanea el código con Google Authenticator, Microsoft Authenticator o similar y escribe el código de 6 dígitos.
                  </p>
                  {step.qr && <img src={step.qr} alt="Código QR para el autenticador" className="mx-auto bg-[#ffffff] p-2 rounded" width={180} height={180} />}
                  {step.manualKey && (
                    <p className="text-[11px] text-gray-500 mt-2 text-center break-all">
                      Clave manual: <code className="text-gray-300">{step.manualKey}</code>
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mb-5">Abre tu aplicación autenticadora y escribe el código vigente.</p>
              )}
              <Field label="Código de 6 dígitos" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}"
                required autoFocus value={code} onChange={e => setCode(e.target.value)} />
              {error && <p role="alert" className="text-sm text-red-400 mb-3">{error}</p>}
              <button disabled={busy} className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 py-2.5 rounded font-semibold">
                {busy ? 'Verificando…' : 'Ingresar'}
              </button>
              <button type="button" onClick={() => { setStep({ k: 'password' }); setError(''); }}
                className="w-full mt-2 text-xs text-gray-500 hover:text-gray-300">Volver</button>
            </form>
          )}
        </div>
        <p className="text-[11px] text-gray-500 text-center mt-4">¿Olvidó su contraseña o perdió el teléfono? El administrador de VERAX la restablece desde Administración.</p>
        <p className="text-[11px] text-gray-600 text-center mt-1">La sesión se cierra tras 5 minutos sin actividad · Acceso auditado</p>
      </div>
    </div>
  );
};

export const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [aviso, setAviso] = useState('');
  const [restante, setRestante] = useState<number | null>(null);
  const userRef = useRef<SessionUser | null>(null);
  userRef.current = user;

  const adopt = useCallback((t: string) => {
    accessToken = t; lastRefreshAt = Date.now(); lastActivity = 0; markActivity();
    setAviso(''); setUser(decode(t));
  }, []);

  const cerrar = useCallback(async (motivo?: 'inactividad') => {
    try { await axios.post(`${API}/auth/logout`, motivo ? { motivo } : {}); } catch { /* sin efecto */ }
    accessToken = null; setRestante(null); setUser(null);
    try { localStorage.setItem(CLOSE_KEY, String(Date.now())); } catch { /* solo esta pestaña */ }
    if (motivo) setAviso('Su sesión se cerró después de 5 minutos sin actividad. Ingrese de nuevo para continuar.');
  }, []);

  useEffect(() => {
    onSessionLost = () => { accessToken = null; setUser(null); };
    tryRefresh().then(ok => { if (ok && accessToken) { markActivity(); setUser(decode(accessToken)); } setChecking(false); });
  }, []);

  // Actividad del usuario y cierre en otras pestañas.
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, markActivity, { passive: true, capture: true }));
    const onStorage = (e: StorageEvent) => {
      if (e.key === CLOSE_KEY && userRef.current) { accessToken = null; setRestante(null); setUser(null); }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      events.forEach(e => window.removeEventListener(e, markActivity, { capture: true } as any));
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Reloj de la sesión: aviso, cierre por inactividad y renovación solo si hubo uso.
  useEffect(() => {
    if (!user) return;
    const tick = setInterval(() => {
      const idle = Date.now() - lastSeen();
      if (idle >= IDLE_MS) { cerrar('inactividad'); return; }
      setRestante(idle >= IDLE_MS - WARN_MS ? Math.ceil((IDLE_MS - idle) / 1000) : null);
      const huboUso = lastSeen() > lastRefreshAt;
      if (huboUso && tokenExpiresAt(accessToken) - Date.now() < 90_000) {
        tryRefresh().then(ok => { if (!ok) setUser(null); });
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [user, cerrar]);

  const logout = useCallback(() => cerrar(), [cerrar]);

  if (checking) return <div className="min-h-screen bg-gray-950" />;
  if (!user) return <Login onDone={adopt} aviso={aviso} />;
  return (
    <AuthContext.Provider value={{ user, logout }}>
      {children}
      {restante !== null && (
        <div role="alertdialog" aria-modal="true" aria-labelledby="verax-inactividad"
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
          <div className="w-full max-w-sm bg-gray-900 border border-gray-700 rounded-xl p-6 text-center shadow-2xl">
            <h2 id="verax-inactividad" className="text-lg font-semibold mb-2">¿Sigue ahí?</h2>
            <p className="text-sm text-gray-300 mb-1">Por seguridad, la sesión se cerrará por inactividad en</p>
            <p className="text-3xl font-semibold tabular-nums text-amber-300 mb-5">{restante} s</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => { lastActivity = 0; markActivity(); setRestante(null); }} autoFocus
                className="bg-cyan-600 hover:bg-cyan-500 px-4 py-2 rounded font-semibold text-sm">Seguir conectado</button>
              <button onClick={() => cerrar()} className="border border-gray-600 hover:bg-gray-800 px-4 py-2 rounded text-sm">Cerrar sesión</button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
